import React from 'react';

import { useTranslation } from 'react-i18next';

import gtmTracker from '@utils/gtmTracker';

import { EmptyState } from '@shared/components/empty-state';
import { Loader } from '@shared/components/loader';

import { PROMOTION_REQUEST_STATUS, useQueryPromotions } from '@hooks/api/promotions/use-query-promotions';
import { useQueryUserPromotions } from '@hooks/api/promotions/use-query-user-promotions';
import useEnglishTranslation from '@hooks/use-english-translation';

import { PromotionBanner } from '@components/promotion-banner';

import { CompletedPromotionCard } from './components/completed-promotion-card';
import { CurrentPromotionCard } from './components/current-promotion-card';
import * as Styled from './promotions.styled';

const Promotions = () => {
	const { t } = useTranslation('promotions');
	const tEn = useEnglishTranslation(['promotions', 'main']);
	const [promosType, setPromosType] = React.useState<PROMOTION_REQUEST_STATUS>(PROMOTION_REQUEST_STATUS.CURRENT);

	const { data: promotions, isLoading } = useQueryPromotions(promosType);
	const { data: userPromotions } = useQueryUserPromotions(100, 1);

	const handleParticipation = (promoId: string) => {
		return userPromotions ? userPromotions.lotteries.some(userPromo => userPromo.id === promoId) : false;
	};

	const handleSwitchTab = (type: PROMOTION_REQUEST_STATUS, label: string) => {
		gtmTracker.trackEvent('click', {
			event_category: 'Promotions',
			event_action: 'click',
			event_label: label
		});
		setPromosType(type);
	};

	return (
		<Styled.Wrapper>
			<Styled.SwitchRow>
				<Styled.SwitchContainer>
					<Styled.SwitchButton
						onClick={() => handleSwitchTab(PROMOTION_REQUEST_STATUS.CURRENT, tEn('main:current_plural'))}
						$isActive={promosType === PROMOTION_REQUEST_STATUS.CURRENT}
					>
						{t('main:current_plural')}
					</Styled.SwitchButton>
					<Styled.SwitchButton
						onClick={() => handleSwitchTab(PROMOTION_REQUEST_STATUS.COMPLETED, tEn('main:ended'))}
						$isActive={promosType === PROMOTION_REQUEST_STATUS.COMPLETED}
					>
						{t('main:ended')}
					</Styled.SwitchButton>
				</Styled.SwitchContainer>
			</Styled.SwitchRow>

			{(!promotions || promotions.data.length === 0) && !isLoading && <EmptyState />}

			{isLoading && <Loader />}

			{promosType === PROMOTION_REQUEST_STATUS.CURRENT ? (
				<Styled.CurrentContainer>
					{promotions &&
						promotions.data.map(
							(promo, index) =>
								promo.main && (
									<PromotionBanner
										key={'main-promo' + index}
										isParticipating={handleParticipation(promo.id)}
										promo={promo}
									/>
								)
						)}
					<Styled.CurrentCards>
						{promotions &&
							promotions.data.map(
								(promo, index) =>
									!promo.main && (
										<CurrentPromotionCard
											key={'promo' + index}
											promo={promo}
											isParticipating={handleParticipation(promo.id)}
										/>
									)
							)}
					</Styled.CurrentCards>
				</Styled.CurrentContainer>
			) : (
				<Styled.CompletedCards>
					{promotions &&
						promotions.data
							.reverse()
							.map((promo, index) => <CompletedPromotionCard key={'ended' + index} promo={promo} />)}
				</Styled.CompletedCards>
			)}
		</Styled.Wrapper>
	);
};

export default Promotions;
